import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Phase-wise Review & Approve page — an NPD Manager / Project Leader reviews each APQP
// task deliverable and Approves (Reviewed) or Rejects with a comment. Independent of the
// task's Complete/Incomplete status. Approve/Reject is gated on the apqp.approve permission.
export default function ApqpReview() {
  const { apqpId } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const phaseFilter = searchParams.get("phase") || "";
  const checkpointId = searchParams.get("checkpoint") || "";
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState({});   // task_id -> draft comment
  const [busyId, setBusyId] = useState(null);

  const canApprove = hasPermission("apqp.approve");

  async function load() {
    try {
      const r = await api.get(`/apqp/${apqpId}/review`);
      setData(r.data);
      const c = {};
      (r.data.tasks || []).forEach((t) => { c[t.id] = t.review_comment || ""; });
      setComments(c);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load review list");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apqpId]);

  async function decide(taskId, decision) {
    setBusyId(taskId + decision);
    try {
      await api.post(`/apqp/${apqpId}/tasks/${taskId}/review`, { decision, comment: comments[taskId] || "" });
      toast.success(decision === "reviewed" ? "Approved (Reviewed)" : decision === "rejected" ? "Rejected" : "Reopened");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="p-6 text-sm text-slate-400">Loading review list…</div>;
  if (!data) return <div className="p-6 text-sm text-red-500">Could not load this project.</div>;

  const rawTasks = data.tasks || [];
  // A "review checkpoint" is an approval task (e.g. "Review of phase 3 …",
  // "Review of special Characteristics …") — never itself a reviewable deliverable.
  const isCheckpoint = (t) => /review of /i.test(t.task_name || "");
  const checkpointTask = checkpointId ? rawTasks.find((t) => t.id === checkpointId) : null;

  let tasks;
  let heading = "Project Deliverables / Review";
  if (checkpointTask) {
    // Scope to the deliverables this checkpoint signs off: same-phase tasks that fall
    // after the previous checkpoint and before this one (matching the SMART-APQP flow).
    const phase = checkpointTask.phase || "";
    const cpSeq = checkpointTask.seq ?? 0;
    const prevSeq = Math.max(
      -1,
      ...rawTasks.filter((t) => (t.phase || "") === phase && isCheckpoint(t) && (t.seq ?? 0) < cpSeq).map((t) => t.seq ?? 0)
    );
    tasks = rawTasks.filter((t) => (t.phase || "") === phase && !isCheckpoint(t) && (t.seq ?? 0) > prevSeq && (t.seq ?? 0) < cpSeq);
    heading = `Approval — ${checkpointTask.task_name}`;
  } else {
    const allTasks = rawTasks.filter((t) => !isCheckpoint(t));
    // When opened from a "Review of phase …" task, show only that phase's tasks.
    tasks = phaseFilter ? allTasks.filter((t) => (t.phase || "") === phaseFilter) : allTasks;
    if (phaseFilter) heading = `Project Deliverables / Review — ${phaseFilter}`;
  }
  const reviewedCount = tasks.filter((t) => t.review_status === "reviewed").length;

  // Status box reflects the task's completion — auto-flips to Completed when approved.
  const statusBadge = (t) => {
    const complete = t.status === "complete";
    return (
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${complete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
        {complete ? "Completed" : "Incomplete"}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => nav(`/apqp/${apqpId}`)} className="text-slate-400 hover:text-slate-700" title="Back to project">
          <ArrowLeft size={20} />
        </button>
        <ShieldCheck size={20} className="text-teal-600" />
        <h1 className="text-xl font-bold text-slate-800">{heading}</h1>
      </div>
      <div className="ml-9 mb-4 text-sm text-slate-500">
        {data.part_name} <span className="font-mono text-slate-400">{data.part_number || data.apqp_number}</span>
        <span className="ml-3 text-emerald-600 font-medium">{reviewedCount}/{tasks.length} reviewed</span>
        {!canApprove && <span className="ml-3 text-amber-600">· View only — you don't have approve rights</span>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-teal-700 text-white text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Task Name</th>
                <th className="text-left px-4 py-2.5 font-semibold">Deliverable / Form</th>
                <th className="text-left px-4 py-2.5 font-semibold w-36">File</th>
                <th className="text-left px-4 py-2.5 font-semibold w-32">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold w-72">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tasks.map((t) => {
                const reviewed = t.review_status === "reviewed";
                return (
                  <tr key={t.id} className="hover:bg-slate-50 align-top" data-testid={`review-row-${t.id}`}>
                    <td className="px-4 py-3 text-slate-800">
                      {t.task_name}{t.mandatory && <span className="text-red-500">*</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {(t.deliverables || []).map((d, i) => (
                        <div key={i}>{d.name}{d.document_no ? <span className="text-slate-400 font-mono text-xs"> · {d.document_no}</span> : null}</div>
                      ))}
                      {(t.deliverables || []).length === 0 && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {t.file_attached
                        ? <span className="text-xs text-emerald-600 font-medium">File Attached</span>
                        : <span className="text-xs text-slate-400">File Not Attached</span>}
                    </td>
                    <td className="px-4 py-3">{statusBadge(t)}</td>
                    <td className="px-4 py-3">
                      {!canApprove ? (
                        <span className="text-slate-300 text-sm">—</span>
                      ) : reviewed ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 uppercase">Reviewed</span>
                          <button onClick={() => decide(t.id, "pending")} disabled={busyId === t.id + "pending"}
                            className="text-xs text-slate-500 hover:text-slate-700 underline disabled:opacity-50">Reopen</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <input
                            value={comments[t.id] || ""}
                            onChange={(e) => setComments((c) => ({ ...c, [t.id]: e.target.value }))}
                            placeholder="Comment.."
                            className="flex-1 min-w-0 border border-slate-200 rounded px-2 py-1.5 text-xs"
                            data-testid={`review-comment-${t.id}`}
                          />
                          <button onClick={() => decide(t.id, "reviewed")} disabled={busyId === t.id + "reviewed"}
                            className="shrink-0 w-8 h-8 rounded bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-50"
                            title="Approve (Reviewed)" data-testid={`review-approve-${t.id}`}>
                            <Check size={16} />
                          </button>
                          <button onClick={() => decide(t.id, "rejected")} disabled={busyId === t.id + "rejected"}
                            className="shrink-0 w-8 h-8 rounded bg-red-500 hover:bg-red-600 text-white flex items-center justify-center disabled:opacity-50"
                            title="Reject" data-testid={`review-reject-${t.id}`}>
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      {t.review_status === "rejected" && t.review_comment && (
                        <div className="mt-1 text-[11px] text-red-500">Rejected: {t.review_comment}</div>
                      )}
                      {reviewed && t.reviewed_by_name && (
                        <div className="mt-1 text-[10px] text-slate-400">by {t.reviewed_by_name}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tasks.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No tasks to review.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
